import React from "react";
import ScheduleList from "../../../DetailsTab/Components/ScheduleList";
import BoxToggleSlider from "../../../../../../../../components/BoxToggleSlider";
import Label from "../../../../../../../../components/Label";
import SizeBox from "../../../../../../../../components/SizeBox";
import {
  DAY_CUSTOM_TIME,
  OPERATION_HOUR_INIT,
} from "../../../../../../../App/configs/DateTimeConfigs";

const SCHEDULE_TYPE = "openHour";

const _capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "";

const _defaultWeekDays = () =>
  OPERATION_HOUR_INIT.find(({ type }: any) => type === SCHEDULE_TYPE)
    ?.week_days || [];

// Build a full Monday..Sunday list, filling days missing from `source` with defaults
const _normalizeWeekDays = (source: any[] = []) =>
  _defaultWeekDays().map((defaultDay: any) => {
    const day = source.find(
      (item: any) =>
        parseInt(item?.week_day) === defaultDay.week_day ||
        item?.week_day_title?.toLowerCase() ===
          defaultDay.week_day_title.toLowerCase()
    );

    if (!day) return { ...defaultDay };

    return {
      week_day: defaultDay.week_day,
      week_day_title:
        _capitalize(day.week_day_title) || defaultDay.week_day_title,
      open_type: parseInt(day.open_type) || defaultDay.open_type,
      from_time: day.from_time || defaultDay.from_time,
      to_time: day.to_time || defaultDay.to_time,
    };
  });

// Initial form values from the room detail (schedule null = follow property hours)
export const getMeetingRoomScheduleInitValues = (
  roomSchedule: any,
  propertySchedule: any
) => {
  const propertyWeekDays = propertySchedule?.operation_hour?.find(
    ({ type }: any) => type === SCHEDULE_TYPE
  )?.week_days;

  return {
    use_property_hours: !roomSchedule,
    schedule_week_days: _normalizeWeekDays(
      roomSchedule?.week_days || propertyWeekDays
    ),
  };
};

// Value for the `schedule` field of PUT /meeting-room
export const getMeetingRoomSchedulePayload = (
  usePropertyHours: boolean,
  weekDays: any[]
) => {
  if (usePropertyHours) return null;

  return {
    week_days: weekDays.map((day: any) => {
      const isCustom = String(day.open_type) === DAY_CUSTOM_TIME;
      return {
        week_day_title: day.week_day_title.toLowerCase(),
        open_type: parseInt(day.open_type),
        from_time: isCustom ? day.from_time : "00:00",
        to_time: isCustom ? day.to_time : "00:00",
      };
    }),
  };
};

export const meetingRoomScheduleValidation = (yup: any) =>
  yup
    .array()
    .test("closing-after-opening", function (this: any, weekDays: any[] = []) {
      if (this.parent.use_property_hours) return true;

      const invalidDay = weekDays.find(
        (day: any) =>
          String(day.open_type) === DAY_CUSTOM_TIME &&
          day.to_time <= day.from_time
      );

      return invalidDay
        ? this.createError({
            message: `Closing time must be after opening time on ${invalidDay.week_day_title}`,
          })
        : true;
    });

type Props = {
  usePropertyHours: boolean;
  weekDays: any[];
  setFieldValue: any;
  error?: any;
};

const MeetingRoomSchedule = ({
  usePropertyHours,
  weekDays,
  setFieldValue,
  error,
}: Props) => {
  const _onChangeSchedule = (value: any) => {
    const newWeekDays = weekDays.map((day: any) => {
      if (day.week_day !== value.week_day) return day;

      return {
        ...day,
        ...(value.open_type ? { open_type: parseInt(value.open_type) } : {}),
        ...(value.from_time ? { from_time: value.from_time } : {}),
        ...(value.to_time ? { to_time: value.to_time } : {}),
      };
    });
    setFieldValue("schedule_week_days", newWeekDays);
  };

  return (
    <div className={"meeting-room-schedule"}>
      <Label fontSize={"12px"} label={"Opening Hours"} className={"sm"} />
      <div className={"fs-12 mb-16px"} style={{ color: "#6C757D" }}>
        {"Room hours can't extend beyond the property's opening hours."}
      </div>
      <BoxToggleSlider
        value={usePropertyHours}
        onChange={(e: any, data: any) =>
          setFieldValue("use_property_hours", data.checked)
        }
        content={
          <Label
            fontSize={"14px"}
            size={"16px"}
            label={"Use property opening hours"}
            className={"mb-0"}
          />
        }
      />
      {!usePropertyHours && (
        <>
          <SizeBox height={"16px"} />
          <ScheduleList
            hourGroupData={{ type: SCHEDULE_TYPE, week_days: weekDays }}
            onChangeSchedule={_onChangeSchedule}
          />
        </>
      )}
      {error ? <span className={"error"}>{String(error)}</span> : null}
    </div>
  );
};

export default MeetingRoomSchedule;
